/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useRef, createContext, useContext, } from 'react';
import { usePopper } from 'react-popper';
import { createPortal } from 'react-dom';
import { oneOf, bool, func, node, string, object, instanceOf, number, } from 'prop-types';
import setClass from '../../common/set-class';
import useOutsideClick from '../../hooks/use-outside-click';
export const PLACEMENT = {
    topStart: 'top-start',
    top: 'top',
    topEnd: 'top-end',
    rightStart: 'right-start',
    right: 'right',
    rightEnd: 'right-end',
    bottomStart: 'bottom-start',
    bottom: 'bottom',
    bottomEnd: 'bottom-end',
    leftStart: 'left-start',
    left: 'left',
    leftEnd: 'left-end',
};
export const TRIGGER = {
    click: ['onClick', 'onClick'],
    hover: ['onMouseOver', 'onMouseLeave'],
    press: ['onMouseDown', 'onMouseUp', 'onMouseLeave'],
};
const DEFAULT_OFFSETS = {
    arrow: 8,
    noArrow: 1,
};
const DropdownContext = createContext({
    close: () => { },
    name: null,
});
export function useDropdownContext() {
    return useContext(DropdownContext);
}
const Dropdown = ({ isOpen, isDisabled, onChange, onOpen, onClose, renderPlace, fitTriggerWidth, closeOnClickOutside, placement, withArrow, render, children, className, style, dropdownClassName, dropdownStyle, trigger, offset, updateRef, name, }) => {
    // if isOpen prop passed then this values ignored
    const [innerIsOpen, setInnerIsOpen] = useState(false);
    const firstUpdate = useRef(true);
    const [triggerElement, setTriggerElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const offsetType = withArrow ? 'arrow' : 'noArrow';
    const { styles, attributes, update } = usePopper(triggerElement, popperElement, {
        placement,
        modifiers: [
            { name: 'arrow', options: { element: arrowElement } },
            {
                name: 'offset',
                options: {
                    offset: [0, offset == null ? DEFAULT_OFFSETS[offsetType] : offset],
                },
            },
            { name: 'flip', enabled: true },
        ],
    });
    useEffect(() => {
        if (updateRef && update) {
            updateRef.update = update;
        }
    }, [updateRef, update]);
    const isOpenValue = isOpen == null ? innerIsOpen : isOpen;
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        if (isOpenValue) {
            if (onOpen) {
                onOpen();
            }
        }
        else if (onClose) {
            onClose();
        }
    }, [isOpenValue]);
    const handleChange = (newValue) => {
        if (isDisabled) {
            return;
        }
        setInnerIsOpen(newValue);
        if (onChange) {
            onChange(newValue);
        }
    };
    useOutsideClick({ current: popperElement }, (event) => {
        if (!closeOnClickOutside) {
            return;
        }
        if (event.target.closest('.b-dropdown-trigger') === triggerElement) {
            return;
        }
        handleChange(false);
    }, 
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'null' is not assignable to type 'never'.
    [popperElement, closeOnClickOutside]);
    const getPopperStyles = () => {
        if (!triggerElement || !fitTriggerWidth) {
            return {};
        }
        return {
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            width: triggerElement.offsetWidth,
        };
    };
    const close = () => handleChange(false);
    const toggle = () => handleChange(!isOpenValue);
    const open = () => handleChange(true);
    const popper = isOpenValue && (React.createElement("div", { className: setClass('b-dropdown-body', dropdownClassName), 
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'Dispatch<SetStateAction<null>>' is not assig... Remove this comment to see the full error message
        ref: setPopperElement, style: {
            ...styles.popper,
            ...getPopperStyles(),
            ...dropdownStyle,
        }, ...attributes.popper },
        React.createElement(DropdownContext.Provider, { value: { close, name } }, render(close)),
        withArrow && (React.createElement("div", { className: "b-dropdown-arrow", "data-popper-arrow": true, 
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'Dispatch<SetStateAction<null>>' is not assig... Remove this comment to see the full error message
            ref: setArrowElement, style: styles.arrow }))));
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const events = TRIGGER[trigger];
    const sameEvent = events[0] === events[1];
    const triggerProps = { [events[0]]: sameEvent ? toggle : open };
    if (!sameEvent) {
        events.slice(1).forEach((evName) => {
            triggerProps[evName] = close;
        });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ...triggerProps, style: style, className: setClass('b-dropdown-trigger', className, isOpenValue && 'b-dropdown-trigger--open'), 
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'Dispatch<SetStateAction<null>>' is not assig... Remove this comment to see the full error message
            ref: setTriggerElement }, children),
        isOpenValue &&
            (renderPlace ? createPortal(popper, renderPlace) : popper)));
};
Dropdown.propTypes = {
    isOpen: bool,
    isDisabled: bool,
    onChange: func,
    onOpen: func,
    onClose: func,
    children: node.isRequired,
    render: func.isRequired,
    renderPlace: instanceOf(Element),
    placement: oneOf([...Object.values(PLACEMENT)]),
    closeOnClickOutside: bool,
    withArrow: bool,
    className: string,
    style: object,
    dropdownClassName: string,
    dropdownStyle: object,
    fitTriggerWidth: bool,
    trigger: oneOf([...Object.keys(TRIGGER)]),
    offset: number,
    updateRef: object,
    name: string,
};
Dropdown.defaultProps = {
    isOpen: undefined,
    isDisabled: false,
    onChange: undefined,
    onOpen: undefined,
    onClose: undefined,
    renderPlace: undefined,
    placement: undefined,
    closeOnClickOutside: true,
    withArrow: false,
    fitTriggerWidth: true,
    className: undefined,
    style: undefined,
    dropdownClassName: undefined,
    dropdownStyle: undefined,
    trigger: 'click',
    offset: null,
    updateRef: null,
    name: null,
};
export default Dropdown;
